import React, { useState } from "react";
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  TextInput,
  Box,
  ColorInput,
} from "@mantine/core";
import { IconChevronLeft } from "@tabler/icons-react";
import CustomRichTextEditor from "../../common/RichTextEditor";
import {
  hasFieldError,
  getFieldErrorMessage,
  getInputStyles,
  getLabelStyles,
  getLabelText,
} from "../utils/validation";

const COLOR_TEXT_BLACK = "#000000";
const COLOR_BACKGROUND_WHITE = "#FFFFFF";

interface BannerContent {
  sectionName?: string;
  text?: string;
  textColor?: string;
  backgroundColor?: string;
}

interface WebsiteGeneratorBannerProps {
  bannerContent?: BannerContent;
  onContentChange: (content: BannerContent) => void;
  onBack: () => void;
  missingFields?: string[];
  requiredValidation?: { [field: string]: boolean };
}

const WebsiteGeneratorBanner: React.FC<WebsiteGeneratorBannerProps> = ({
  bannerContent,
  onContentChange,
  onBack,
  missingFields = [],
  requiredValidation = {},
}) => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const handleSectionNameChange = (value: string) => {
    if (value.length > 100) {
      setValidationErrors((prev) => ({
        ...prev,
        sectionName: "Section name cannot exceed 100 characters",
      }));
      return;
    }
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.sectionName;
      return next;
    });
    onContentChange({
      ...(bannerContent || {}),
      sectionName: value,
    });
  };

  const handleTextChange = (value: string) => {
    const wordCount = value
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
    if (wordCount > 250) {
      setValidationErrors((prev) => ({
        ...prev,
        text: "Text cannot exceed 250 words",
      }));
      return;
    }
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.text;
      return next;
    });
    onContentChange({
      ...(bannerContent || {}),
      text: value,
    });
  };

  const handleTextColorChange = (value: string) => {
    onContentChange({
      ...(bannerContent || {}),
      textColor: value,
    });
  };

  const handleBackgroundColorChange = (value: string) => {
    onContentChange({
      ...(bannerContent || {}),
      backgroundColor: value,
    });
  };

  const getWordCount = (text?: string) => {
    if (!text) return 0;
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between" align="center">
        <ActionIcon variant="subtle" onClick={onBack}>
          <IconChevronLeft size={16} />
        </ActionIcon>
        <Text fw={600} size="lg">
          Banner
        </Text>
      </Group>

      {/* Section Name */}
      <Box>
        <Text
          size="sm"
          fw={500}
          mb="xs"
          style={getLabelStyles(
            "sectionName",
            requiredValidation,
            bannerContent?.sectionName,
          )}
        >
          {getLabelText(
            "Section Name",
            "sectionName",
            requiredValidation,
            bannerContent?.sectionName,
          )}
        </Text>
        <TextInput
          placeholder="Banner"
          value={bannerContent?.sectionName || ""}
          onChange={(event) =>
            handleSectionNameChange(event.currentTarget.value)
          }
          error={getFieldErrorMessage(
            "sectionName",
            requiredValidation,
            validationErrors,
            bannerContent?.sectionName,
          )}
          maxLength={100}
          styles={getInputStyles(
            "sectionName",
            requiredValidation,
            missingFields,
            undefined,
            bannerContent?.sectionName,
          )}
        />
      </Box>

      {/* Text */}
      <Box>
        <Text
          size="sm"
          fw={500}
          mb="xs"
          style={getLabelStyles(
            "text",
            requiredValidation,
            bannerContent?.text,
          )}
        >
          {getLabelText(
            "Text",
            "text",
            requiredValidation,
            bannerContent?.text,
          )}
        </Text>
        <CustomRichTextEditor
          label=""
          placeholder="Enter banner text..."
          value={bannerContent?.text || ""}
          onChange={(htmlValue) => handleTextChange(htmlValue)}
          minHeight={180}
        />
        {hasFieldError("text", requiredValidation, bannerContent?.text) && (
          <Text size="xs" c="red" mt="xs">
            Required
          </Text>
        )}
        {validationErrors.text && (
          <Text size="xs" c="red" mt="xs">
            {validationErrors.text}
          </Text>
        )}
        <Text size="xs" c="dimmed" mt="xs">
          {getWordCount(bannerContent?.text)} words
        </Text>
      </Box>

      {/* Text Color */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Text Color
        </Text>
        <ColorInput
          value={bannerContent?.textColor || COLOR_TEXT_BLACK}
          onChange={handleTextColorChange}
          placeholder={COLOR_TEXT_BLACK}
        />
      </Box>

      {/* Background Color */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Background Color
        </Text>
        <ColorInput
          value={bannerContent?.backgroundColor || COLOR_BACKGROUND_WHITE}
          onChange={handleBackgroundColorChange}
          placeholder={COLOR_BACKGROUND_WHITE}
        />
      </Box>
    </Stack>
  );
};

export default WebsiteGeneratorBanner;
